import { useEffect, useMemo, useState } from 'react';

import { ReactComponent as WarningIcon } from '../../../icons/warning.svg';

import { useAppNavigation } from '../../../hooks/useAppNavigation';
import { useAuth } from '../../../hooks/useAuth';
import { useMealPlanner } from '../../../hooks/useMealPlanner';

import './meal-planner.scss';

import { Button } from '../../common/button/Button';
import { Stepper } from '../../common/stepper/Stepper';
import { ExclusionsStep } from './steps/ExclusionsStep';
import { GoalsStep } from './steps/GoalsStep';
import { SlotsStep } from './steps/SlotsStep';
import { TargetsStep } from './steps/TargetsStep';
import { TimeframeStep } from './steps/TimeframeStep';
import { PlanResults } from './results/PlanResults';

const STEP_LABELS = ['Dates', 'Meals', 'Goal', 'Targets', 'Preferences'];

const INITIAL_DATA = {
  startDate: null,
  endDate: null,
  slots: [],
  goal: null,
  bodyStats: { weightKg: '', heightCm: '', age: '', biologicalSex: '', activityLevel: '' },
  targets: null,
  excludedTags: [],
  excludedItemIds: [],
  varietyLevel: 'SOME'
};

export const MealPlanner = () => {
  const { user } = useAuth();
  const { setShowMealPlannerModal } = useAppNavigation();
  const { getSlotTemplates, calculateTargets, generatePlan, regeneratePlan, updateSelections, getPreferences } =
    useMealPlanner();

  const [step, setStep] = useState(0);
  const [data, setData] = useState(INITIAL_DATA);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    getSlotTemplates()
      .then(setTemplates)
      .finally(() => setLoadingTemplates(false));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!user || user.isAnonymous) return;
    getPreferences(user.uid)
      .then((prefs) => {
        if (!prefs) return;
        setData((d) => ({
          ...d,
          goal: prefs.goal ?? d.goal,
          bodyStats: {
            weightKg: prefs.weightKg ?? d.bodyStats.weightKg,
            heightCm: prefs.heightCm ?? d.bodyStats.heightCm,
            age: prefs.age ?? d.bodyStats.age,
            biologicalSex: prefs.biologicalSex ?? d.bodyStats.biologicalSex,
            activityLevel: prefs.activityLevel ?? d.bodyStats.activityLevel
          },
          excludedTags: prefs.excludedTags ?? d.excludedTags,
          excludedItemIds: prefs.excludedItemIds ?? d.excludedItemIds
        }));
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [user?.uid]);

  const patch = (fields) => setData((d) => ({ ...d, ...fields }));

  const percentSum = data.slots.reduce((sum, s) => sum + (Number(s.percent) || 0), 0);

  const stepValid = useMemo(() => {
    switch (step) {
      case 0:
        return !!data.startDate && !!data.endDate && data.endDate >= data.startDate;
      case 1:
        return data.slots.length > 0 && data.slots.every((s) => s.name?.trim());
      case 2:
        return (
          !!data.goal &&
          data.bodyStats.weightKg &&
          data.bodyStats.heightCm &&
          data.bodyStats.age &&
          data.bodyStats.biologicalSex &&
          data.bodyStats.activityLevel
        );
      case 3:
        return !!data.targets && Math.abs(percentSum - 100) < 0.5;
      default:
        return true;
    }
  }, [step, data, percentSum]);

  const goNext = () => {
    if (step === 2) {
      patch({ targets: null }); // force target recompute when goal/body stats step is completed
    }
    if (step < STEP_LABELS.length - 1) {
      setStep(step + 1);
    } else {
      handleGenerate();
    }
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const handleGenerate = () => {
    setGenerating(true);
    setGenerateError(null);
    generatePlan({
      startDate: data.startDate,
      endDate: data.endDate,
      slots: data.slots,
      dailyTargets: data.targets,
      goal: data.goal,
      varietyLevel: data.varietyLevel,
      excludedTags: data.excludedTags,
      excludedItemIds: data.excludedItemIds
    })
      .then(setPlan)
      .catch(() => setGenerateError('Could not generate your plan. Please try again.'))
      .finally(() => setGenerating(false));
  };

  if (plan) {
    return (
      <PlanResults
        plan={plan}
        onPlanChange={setPlan}
        regeneratePlan={regeneratePlan}
        updateSelections={updateSelections}
        onClose={() => setShowMealPlannerModal(false)}
      />
    );
  }

  return (
    <div className="meal-planner">
      <div className="meal-planner__header">
        <h1 className="meal-planner__title">Plan my meals</h1>
        <Stepper steps={STEP_LABELS} current={step} />
      </div>

      <div className="meal-planner__body">
        {step === 0 && (
          <TimeframeStep
            startDate={data.startDate}
            endDate={data.endDate}
            onChange={(range) => patch(range)}
          />
        )}
        {step === 1 && (
          <SlotsStep
            slots={data.slots}
            onChange={(slots) => patch({ slots })}
            templates={templates}
            loadingTemplates={loadingTemplates}
          />
        )}
        {step === 2 && (
          <GoalsStep
            goal={data.goal}
            bodyStats={data.bodyStats}
            onGoalChange={(goal) => patch({ goal })}
            onBodyStatsChange={(bodyStats) => patch({ bodyStats })}
          />
        )}
        {step === 3 && (
          <TargetsStep
            goal={data.goal}
            bodyStats={data.bodyStats}
            slots={data.slots}
            targets={data.targets}
            onTargetsChange={(targets) => patch({ targets })}
            onSlotsChange={(slots) => patch({ slots })}
            calculateTargets={calculateTargets}
          />
        )}
        {step === 4 && (
          <ExclusionsStep
            excludedTags={data.excludedTags}
            excludedItemIds={data.excludedItemIds}
            varietyLevel={data.varietyLevel}
            onChange={(fields) => patch(fields)}
          />
        )}
      </div>

      {generateError && (
        <div className="meal-planner__error">
          <WarningIcon width={14} height={14} />
          {generateError}
        </div>
      )}

      <div className="meal-planner__footer">
        <Button onClick={goNext} disabled={!stepValid} loading={generating} large>
          {step === STEP_LABELS.length - 1 ? 'Generate My Plan' : 'Continue'}
        </Button>
        {step > 0 && (
          <Button secondary onClick={goBack} large disabled={generating}>
            Back
          </Button>
        )}
      </div>
    </div>
  );
};
